import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Printer, FileDown, ArrowLeft, Package } from "lucide-react";
import { api, API, etTime } from "@/lib/api";

export default function PackingSlip() {
  const { orderNumber } = useParams();
  const [slip, setSlip] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    api.get(`/admin/orders/${orderNumber}/packing-slip`)
      .then((r) => setSlip(r.data))
      .catch(() => setError(true));
  }, [orderNumber]);

  if (error)
    return (
      <div className="min-h-screen flex items-center justify-center text-navy/60">
        Could not load packing slip — are you signed in as admin?&nbsp;
        <Link to="/admin/login" className="text-brick font-bold">Sign in</Link>
      </div>
    );
  if (!slip) return <div className="min-h-screen flex items-center justify-center text-navy/50">Loading packing slip…</div>;

  const addr = slip.shipping_address || {};
  const isPickup = slip.fulfillment === "pickup";

  return (
    <div data-testid="packing-slip-page" className="min-h-screen bg-white text-black max-w-3xl mx-auto p-8 print:p-0">
      {/* Action bar — hidden when printing */}
      <div className="print:hidden flex items-center justify-between flex-wrap gap-3 mb-6">
        <Link to={`/admin/orders/${orderNumber}`} data-testid="slip-back" className="inline-flex items-center gap-2 text-sm font-semibold text-navy hover:text-brick">
          <ArrowLeft className="w-4 h-4" /> Back to order
        </Link>
        <div className="flex gap-2">
          <button data-testid="slip-print-btn" onClick={() => window.print()}
            className="inline-flex items-center gap-2 bg-navy text-white font-bold text-sm rounded-md px-4 py-2.5">
            <Printer className="w-4 h-4" /> Print Packing Slip
          </button>
          <a data-testid="slip-pdf-btn" href={`${API}/admin/orders/${orderNumber}/packing-slip.pdf`}
            className="inline-flex items-center gap-2 bg-brick text-white font-bold text-sm rounded-md px-4 py-2.5">
            <FileDown className="w-4 h-4" /> Download PDF
          </a>
        </div>
      </div>

      {/* Slip header */}
      <div className="bg-navy text-white px-6 py-4 flex items-center justify-between rounded-t-lg print:rounded-none">
        <span className="font-display text-2xl">JOHNNY<span className="text-gold">MART</span></span>
        <span className="text-xs font-bold uppercase tracking-wide">Packing Slip — Internal</span>
      </div>

      <div className="border border-t-0 border-navy/20 rounded-b-lg p-6">
        <div className="grid grid-cols-2 gap-6 text-sm">
          <div>
            <p className="font-bold text-lg" data-testid="slip-order-number">Order {slip.order_number}</p>
            <p className="text-black/60 mt-1">Order date: {slip.order_date ? etTime(slip.order_date, false) : "—"}</p>
            <p className="text-black/60">Shipping method: <b className="text-black">{slip.shipping_method}</b></p>
            {slip.tracking_number && <p className="text-black/60">Tracking #: <b className="text-black font-mono">{slip.tracking_number}</b></p>}
          </div>
          <div>
            <p className="font-bold uppercase text-xs tracking-wide text-black/50">Ship to / Customer</p>
            <p className="font-semibold mt-1" data-testid="slip-customer">{slip.customer_name}</p>
            {isPickup ? (
              <p>LOCAL PICKUP — Ponte Vedra Beach, FL</p>
            ) : (
              <>
                <p>{addr.line1} {addr.line2}</p>
                <p>{addr.city}, {addr.state} {addr.postal_code}</p>
              </>
            )}
            <p className="text-black/60">{slip.customer_email}</p>
            {slip.customer_phone && <p className="text-black/60">{slip.customer_phone}</p>}
          </div>
        </div>

        <table className="w-full text-sm mt-6 border-collapse">
          <thead>
            <tr className="bg-navy text-white text-left text-xs uppercase">
              <th className="px-2 py-2 w-8">✓</th>
              <th className="px-2 py-2 w-14"></th>
              <th className="px-2 py-2">Product</th>
              <th className="px-2 py-2 w-12">Qty</th>
              <th className="px-2 py-2 w-24">Inventory #</th>
              <th className="px-2 py-2 w-28">Storage</th>
              <th className="px-2 py-2 w-16">Weight</th>
            </tr>
          </thead>
          <tbody>
            {slip.items.map((it, i) => (
              <tr key={i} data-testid={`slip-item-${i}`} className="border-b border-black/10 align-top">
                <td className="px-2 py-3"><span className="inline-block w-4 h-4 border-2 border-navy rounded-sm" /></td>
                <td className="px-2 py-3">
                  {it.image && <img src={it.image} alt="" className="w-12 h-12 object-contain bg-[#f5f5f5] rounded" />}
                </td>
                <td className="px-2 py-3">
                  <span className="font-semibold">{it.name}</span>
                  {(it.variant_label || it.condition) && (
                    <span className="block text-xs text-black/50">{[it.variant_label, it.condition].filter(Boolean).join(" · ")}</span>
                  )}
                </td>
                <td className="px-2 py-3 font-semibold">{it.quantity}</td>
                <td className="px-2 py-3 font-mono font-bold text-brick">{it.inventory_id || "—"}</td>
                <td className="px-2 py-3">{it.storage_location || "—"}</td>
                <td className="px-2 py-3">{it.weight_oz ? `${it.weight_oz} oz` : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>

        {slip.customer_note && (
          <p className="mt-5 text-sm"><b>Customer note:</b> {slip.customer_note}</p>
        )}
        {slip.internal_notes && (
          <div className="mt-4 rounded-md border border-brick/40 bg-brick/5 p-3 text-sm">
            <b className="text-brick">Internal packing notes (do not include in box):</b>
            <p className="mt-1 whitespace-pre-wrap">{slip.internal_notes}</p>
          </div>
        )}

        <p className="mt-8 text-xs text-black/40 italic">
          JohnnyMart · Ponte Vedra Beach, Florida · johnnymart.net — internal document, not a receipt
        </p>
      </div>
    </div>
  );
}
